import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import {
	Breadcrumbs,
	Button,
	MainContainer,
	UnderwritingBreadcrumbs,
} from "components";
import { LineDivider } from "components/layout";
import { breadcrumbsReportFromMerchantPart } from "helpers";
import { LABELS_GVERIFY_RESULT, getResultTableData } from "mappers";
import {
	useDownloadGVerifyGAuthenticationDocumentMutation,
	useGverifyAuroraOutcomeFieldMutation,
	useGVerifyGAuthenticateResultQuery,
} from "queries";
import {
	getCurrentUserAccessToken,
	getCurrentUserId,
} from "store/user/user.reducer";
import { UnderwritingTabs, UnderwritingRouteMatchProps } from "types";

import ReportTable from "./reportTable";

const MerchantGverifyReport = () => {
	const currentUserId = useSelector(getCurrentUserId);
	const token: string = useSelector(getCurrentUserAccessToken);
	const match: UnderwritingRouteMatchProps = useRouteMatch();
	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/"),
		[match?.url],
	);

	const applicationId = useMemo(
		() =>
			isMerchantPage ? match?.params?.applicationId : match?.params?.id,
		[isMerchantPage, match?.params?.applicationId, match?.params?.id],
	);

	const { data, isFetching, refetch } = useGVerifyGAuthenticateResultQuery({
		id: applicationId,
	});

	const {
		mutate: downloadGVerifyGAuthenticationDocument,
		isLoading: isLoadingDownloadDocument,
	} = useDownloadGVerifyGAuthenticationDocumentMutation();

	const { mutate: submitAuroraOutcome, isLoading: isLoadingAuroraOutcome } =
		useGverifyAuroraOutcomeFieldMutation();

	const submitAuroraOutcomeHandler = useCallback(
		values =>
			submitAuroraOutcome({
				id: applicationId,
				values,
				onSuccessHandler: () => refetch(),
			}),
		[applicationId, refetch, submitAuroraOutcome],
	);

	const downloadDocument = () =>
		downloadGVerifyGAuthenticationDocument({
			id: applicationId,
			token: token,
		});

	const gVerifyGAuthenticateResult = getResultTableData(
		data?.original_inquiry,
		data?.re_run_inquiry,
		LABELS_GVERIFY_RESULT,
	);

	const isRerun = useMemo(
		() => !!data?.re_run_inquiry,
		[data?.re_run_inquiry],
	);

	const editableFields = useMemo(() => {
		let fields = {};
		if (
			!isMerchantPage &&
			match?.params?.tab !== UnderwritingTabs.Unassigned.name &&
			match?.params?.tab !== UnderwritingTabs.Historical.name &&
			match?.params?.tab !== UnderwritingTabs.QAUnassigned.name &&
			match?.params?.tab !== UnderwritingTabs.QAComplete.name &&
			data?.underwriter_id &&
			data?.underwriter_id === currentUserId
		) {
			fields = {
				...fields,
				aurora_outcome: {
					submitHandler: submitAuroraOutcomeHandler,
					isLoading: isLoadingAuroraOutcome,
				},
			};
		}
		return fields;
	}, [
		currentUserId,
		data?.underwriter_id,
		isLoadingAuroraOutcome,
		isMerchantPage,
		match?.params?.tab,
		submitAuroraOutcomeHandler,
	]);

	return (
		<MainContainer>
			{isMerchantPage ? (
				<Breadcrumbs
					pages={breadcrumbsReportFromMerchantPart({
						id: match.params.id,
						title: "gVerify & gAuthenticate Result",
					})}
				/>
			) : (
				<UnderwritingBreadcrumbs match={match} />
			)}
			<div className={isFetching ? "mt-4" : "mt-0"}>
				<ReportTable
					data={gVerifyGAuthenticateResult}
					isRerun={isRerun}
					isLoading={isFetching}
					editableFields={editableFields}
				/>
			</div>
			<LineDivider />
			<Button
				type="button"
				value="Download"
				disabled={isLoadingDownloadDocument}
				onClick={downloadDocument}
				isLoading={isFetching || isLoadingDownloadDocument}
			/>
		</MainContainer>
	);
};

export default MerchantGverifyReport;
